import {
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  SimpleGrid,
  Text,
  useOutsideClick,
  VStack,
} from '@chakra-ui/react';
import { Input as InputComponent, InputGroup, InputRightElement } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Calendar, DateObj, GetBackForwardPropsOptions, RenderProps, useDayzed } from 'dayzed';
import { isEmpty, isNil, map, merge } from 'lodash';
import { FC, Fragment, useRef, useState } from 'react';
import { LuCalendar, LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight, LuX } from 'react-icons/lu';

const MONTH_NAMES_DEFAULT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES_DEFAULT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DATE_FORMAT_DEFAULT = 'yyyy-MM-dd';

interface SingleDatepickerBackButtonsProps {
  calendars: Calendar[];
  getBackProps: (data: GetBackForwardPropsOptions) => Record<string, any>;
}

interface SingleDatepickerForwardButtonsProps {
  calendars: Calendar[];
  getForwardProps: (data: GetBackForwardPropsOptions) => Record<string, any>;
}

export type SingleDatepickerProps = {
  disabled?: boolean;
  date?: Date;
  id?: string;
  name?: string;
  maxDate?: Date;
  minDate?: Date;
  configs?: SingleDatepickerConfigs;
} & (
  | {
      onDateChange: (date: Date | null) => void;
      clearable: true;
    }
  | {
      onDateChange: (date: Date) => void;
      clearable?: never;
    }
);

export interface SingleDatepickerConfigs {
  dateFormat: string;
  monthNames?: string[];
  dayNames?: string[];
}

const transformDisplayDate = (value: any) => {
  const dateMatches =
    value
      .toString()
      .match(
        /(?:(?=.+\.(?:0[13578]|1[02]))(?:[012][1-9]|3[01])|(?=.+\.(?:0[469]|11))(?:[012][1-9]|30)|(?=.+\.02)(?:[01][1-9]|2[1-9]))\.(?:0[1-9]|1[12])\.\d{4}/g,
      ) ?? [];
  if (dateMatches.length > 0) {
    const chunks = value.split('.');
    return new Date(chunks[2], chunks[1] - 1, chunks[0]);
  }
  return false;
};

const SingleDatepickerBackButtons = (props: SingleDatepickerBackButtonsProps) => {
  const { calendars, getBackProps } = props;
  return (
    <Fragment>
      <IconButton
        {...getBackProps({
          calendars,
          offset: 12,
        })}
        icon={<LuChevronsLeft />}
        aria-label="year-left"
        variant="ghost"
      />
      <IconButton {...getBackProps({ calendars })} icon={<LuChevronLeft />} aria-label="month-left" variant="ghost" />
    </Fragment>
  );
};

const SingleDatepickerForwardButtons = (props: SingleDatepickerForwardButtonsProps) => {
  const { calendars, getForwardProps } = props;
  return (
    <Fragment>
      <IconButton {...getForwardProps({ calendars })} icon={<LuChevronRight />} aria-label="month-right" variant="ghost" />
      <IconButton
        {...getForwardProps({
          calendars,
          offset: 12,
        })}
        icon={<LuChevronsRight />}
        aria-label="year-right"
        variant="ghost"
      />
    </Fragment>
  );
};

const SingleDatepickerCalendar = (props: RenderProps & { configs: SingleDatepickerConfigs }) => {
  const { calendars, getDateProps, getBackProps, getForwardProps, configs } = props;

  if (isEmpty(calendars)) {
    return null;
  }

  return (
    <Box>
      <HStack className="datepicker-calendar">
        {map(calendars, (calendar) => {
          return (
            <VStack key={`${calendar.month}${calendar.year}`}>
              <HStack>
                <SingleDatepickerBackButtons calendars={calendars} getBackProps={getBackProps} />
                <Heading size="sm" textAlign="center">
                  {
                    // @ts-ignore
                    configs.monthNames[calendar.month]
                  }{' '}
                  {calendar.year}
                </Heading>
                <SingleDatepickerForwardButtons calendars={calendars} getForwardProps={getForwardProps} />
              </HStack>
              <Divider />
              <SimpleGrid columns={7} spacing={2} textAlign="center">
                {map(configs.dayNames, (day) => (
                  <Box key={`${calendar.month}${calendar.year}${day}`}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {day}
                    </Text>
                  </Box>
                ))}
                {map(calendar.weeks, (week, weekIndex) => {
                  return map(week, (dateObj: DateObj, index) => {
                    const {
                      date,
                      today,
                      // prevMonth,
                      // nextMonth,
                      selected,
                    } = dateObj;
                    const key = `${calendar.month}${calendar.year}${weekIndex}${index}`;

                    return (
                      <Button
                        {...getDateProps({
                          dateObj,
                          // disabled: isDisabled
                        })}
                        key={key}
                        size="sm"
                        variant="outline"
                        borderColor={today ? 'blue.500' : 'transparent'}
                        bgColor={selected ? 'blue.200' : 'transparent'}
                      >
                        {date.getDate()}
                      </Button>
                    );
                  });
                })}
              </SimpleGrid>
            </VStack>
          );
        })}
      </HStack>
    </Box>
  );
};
const defaultConfigs = {
  dateFormat: DATE_FORMAT_DEFAULT,
  monthNames: MONTH_NAMES_DEFAULT,
  dayNames: DAY_NAMES_DEFAULT,
};

export const SingleDatepicker: FC<SingleDatepickerProps> = ({ configs = defaultConfigs, clearable, ...props }) => {
  const { date, minDate, maxDate, name, disabled, onDateChange, id } = props;
  //so that you only have to pass some of the config parameters
  const dateConfigs = merge(defaultConfigs, configs);
  const ref = useRef<HTMLElement>(null);
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  useOutsideClick({
    ref: ref,
    handler: () => setPopoverOpen(false),
  });

  const onDateSelected = (options: { selectable: boolean; date: Date }) => {
    const { selectable, date } = options;
    if (!selectable) return;
    if (!isNil(date)) {
      onDateChange(date);
      setPopoverOpen(false);
      return;
    }
  };

  const dayzedData = useDayzed({
    showOutsideDays: true,
    onDateSelected,
    selected: date,
    maxDate: maxDate,
    minDate: minDate,
  });

  return (
    <Popover
      placement="bottom-start"
      variant="responsive"
      isOpen={popoverOpen}
      onClose={() => setPopoverOpen(false)}
      initialFocusRef={initialFocusRef}
      isLazy
    >
      <PopoverTrigger>
        <InputGroup>
          <InputComponent
            id={id}
            autoComplete="off"
            isDisabled={disabled}
            ref={initialFocusRef}
            onClick={() => setPopoverOpen(!popoverOpen)}
            name={name}
            value={date ? format(new Date(date), dateConfigs.dateFormat) : ''}
            onChange={(e) => {
              const value = transformDisplayDate(e.target.value);
              if (value) {
                onDateSelected({
                  selectable: true,
                  date: value,
                });
              }
            }}
          />
          {clearable && date != null && (
            <InputRightElement color="muted" right="2rem">
              <IconButton
                icon={<LuX />}
                aria-label="clear-input"
                variant="ghost"
                size="sm"
                onClick={() => (onDateChange as (date: Date | null) => void)(null)}
              />
            </InputRightElement>
          )}
          <InputRightElement color="muted" pointerEvents="none">
            <LuCalendar />
          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>
      <Portal>
        <PopoverContent ref={ref}>
          <PopoverBody padding={'10px 5px'} borderWidth={1} borderColor="blue.400">
            <SingleDatepickerCalendar {...dayzedData} configs={dateConfigs} />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
